import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  buildPdlDecisionReviewPathSteps,
  type PdlReviewPathTourStep,
} from '../../../lib/pdlDecision/buildPdlDecisionReviewPathSteps';

export type ActivePdlReviewPathTour = {
  steps: PdlReviewPathTourStep[];
  stepIndex: number;
};

type PdlDecisionTourContextValue = {
  reviewPathTour: ActivePdlReviewPathTour | null;
  technicalOpen: boolean;
  remarksOpen: boolean;
  startReviewPathTour: (options?: { hasEvidence?: boolean }) => void;
  closeReviewPathTour: () => void;
  nextReviewPathStep: () => void;
  prevReviewPathStep: () => void;
  setTechnicalOpen: (open: boolean) => void;
  setRemarksOpen: (open: boolean) => void;
};

const PdlDecisionTourContext = createContext<PdlDecisionTourContextValue | null>(null);

export function usePdlDecisionTour(): PdlDecisionTourContextValue {
  const ctx = useContext(PdlDecisionTourContext);
  if (!ctx) {
    throw new Error('usePdlDecisionTour must be used within PdlDecisionTourProvider');
  }
  return ctx;
}

export function usePdlDecisionTourOptional(): PdlDecisionTourContextValue | null {
  return useContext(PdlDecisionTourContext);
}

export function PdlDecisionTourProvider({ children }: { children: ReactNode }) {
  const [reviewPathTour, setReviewPathTour] = useState<ActivePdlReviewPathTour | null>(null);
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const [remarksOpen, setRemarksOpen] = useState(false);

  const closeReviewPathTour = useCallback(() => {
    setReviewPathTour(null);
    setTechnicalOpen(false);
    setRemarksOpen(false);
  }, []);

  const startReviewPathTour = useCallback((options?: { hasEvidence?: boolean }) => {
    const steps = buildPdlDecisionReviewPathSteps(options);
    setTechnicalOpen(false);
    setRemarksOpen(false);
    setReviewPathTour({ steps, stepIndex: 0 });
  }, []);

  const nextReviewPathStep = useCallback(() => {
    setReviewPathTour((prev) => {
      if (!prev) return prev;
      return { ...prev, stepIndex: Math.min(prev.stepIndex + 1, prev.steps.length - 1) };
    });
  }, []);

  const prevReviewPathStep = useCallback(() => {
    setReviewPathTour((prev) => {
      if (!prev) return prev;
      return { ...prev, stepIndex: Math.max(prev.stepIndex - 1, 0) };
    });
  }, []);

  const value = useMemo(
    () => ({
      reviewPathTour,
      technicalOpen,
      remarksOpen,
      startReviewPathTour,
      closeReviewPathTour,
      nextReviewPathStep,
      prevReviewPathStep,
      setTechnicalOpen,
      setRemarksOpen,
    }),
    [
      reviewPathTour,
      technicalOpen,
      remarksOpen,
      startReviewPathTour,
      closeReviewPathTour,
      nextReviewPathStep,
      prevReviewPathStep,
    ],
  );

  return (
    <PdlDecisionTourContext.Provider value={value}>{children}</PdlDecisionTourContext.Provider>
  );
}
